var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Autocomplete, Chip, Stack, Button, useMediaQuery, Typography, Checkbox, ListItemText, MenuItem, } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import FDrawer from "@ui/components/drawer/FDrawer";
import { useGetAllRolesQuery } from "../../../features/projects/projectsApi";
const schema = yup.object().shape({
    name: yup.string().required("Naam is verplicht"),
    email: yup
        .string()
        .email("Ongeldig e-mailadres")
        .required("E-mail is verplicht"),
    companies: yup
        .array()
        .of(yup.string().required())
        .min(1, "Selecteer minimaal één bedrijf")
        .required("Bedrijven zijn verplicht"),
    status: yup.boolean().default(true),
    roleId: yup.string().required("Rol is verplicht"),
});
const SELECT_ALL_OPTION = "__SELECT_ALL__";
export const AddEditUserDialog = ({ open, onClose, onSave, editUser, availableCompanies, }) => {
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));
    const FORM_ID = "add-edit-user-form";
    const [searchQuery, setSearchQuery] = useState("");
    // Fetch roles from API
    const { data: roles, isLoading: rolesLoading } = useGetAllRolesQuery();
    const { control, handleSubmit, reset, formState: { errors, isValid }, } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            name: "",
            email: "",
            companies: [],
            status: true,
            roleId: "",
        },
        mode: "onChange",
    });
    // Initialize form with edit user data
    useEffect(() => {
        if (open) {
            setSearchQuery(""); // Reset search query when dialog opens
            if (editUser) {
                reset({
                    name: editUser.name,
                    email: editUser.email,
                    companies: editUser.companies, // Show actual company names
                    status: editUser.status,
                    roleId: editUser.roleId || "",
                });
            }
            else {
                reset({
                    name: "",
                    email: "",
                    companies: [],
                    status: true,
                    roleId: "",
                });
            }
        }
    }, [editUser, open, reset]);
    const onSubmit = (data) => __awaiter(void 0, void 0, void 0, function* () {
        const newUser = {
            avatar: "/assets/avatars/avatar-default.jpg",
            name: data.name,
            email: data.email,
            companies: data.companies,
            status: data.status,
            roleId: data.roleId,
        };
        yield onSave(newUser);
        onClose();
    });
    const formContent = (React.createElement(Stack, { spacing: 3, sx: { mt: 2 } },
        React.createElement(Controller, { name: "name", control: control, render: ({ field }) => {
                var _a;
                return (React.createElement(TextField, Object.assign({}, field, { fullWidth: true, label: "Naam", error: !!errors.name, helperText: ((_a = errors.name) === null || _a === void 0 ? void 0 : _a.message) || "" })));
            } }),
        React.createElement(Controller, { name: "email", control: control, render: ({ field }) => {
                var _a;
                return (React.createElement(TextField, Object.assign({}, field, { fullWidth: true, label: "E-mail", type: "email", error: !!errors.email, helperText: ((_a = errors.email) === null || _a === void 0 ? void 0 : _a.message) || "" })));
            } }),
        React.createElement(Controller, { name: "roleId", control: control, render: ({ field }) => {
                var _a;
                return (React.createElement(TextField, Object.assign({}, field, { select: true, fullWidth: true, label: "Rol", error: !!errors.roleId, helperText: ((_a = errors.roleId) === null || _a === void 0 ? void 0 : _a.message) || "", disabled: rolesLoading }), rolesLoading ? (React.createElement(MenuItem, { disabled: true }, "Laden...")) : roles && roles.length > 0 ? (roles
                    .filter((role) => role.isActive)
                    .map((role) => (React.createElement(MenuItem, { key: role.id, value: role.id }, role.name)))) : (React.createElement(MenuItem, { disabled: true }, "Geen rollen beschikbaar"))));
            } }),
        React.createElement(Controller, { name: "companies", control: control, render: (_a) => {
                var _b = _a.field, { onChange, value } = _b, field = __rest(_b, ["onChange", "value"]);
                const selectedCompanies = value || [];
                // Filter companies based on search query
                const filteredCompanies = availableCompanies.filter((company) => company.toLowerCase().includes(searchQuery.toLowerCase()));
                // Sort filtered companies: selected companies first, then unselected
                const sortedCompanies = [...filteredCompanies].sort((a, b) => {
                    const aSelected = selectedCompanies.includes(a);
                    const bSelected = selectedCompanies.includes(b);
                    if (aSelected && !bSelected)
                        return -1;
                    if (!aSelected && bSelected)
                        return 1;
                    return 0;
                });
                // Check if all filtered companies are selected
                const areAllFilteredSelected = filteredCompanies.length > 0 &&
                    filteredCompanies.every((company) => selectedCompanies.includes(company));
                // Check if all available companies are selected (for the overall state)
                const isAllSelected = availableCompanies.length > 0 &&
                    selectedCompanies.length === availableCompanies.length;
                // Create options array with "Select All" at the top, then sorted companies
                const options = [SELECT_ALL_OPTION, ...sortedCompanies];
                const handleChange = (_, newValue) => {
                    // Check if "Select All" option was in the new value
                    const selectAllWasClicked = newValue.includes(SELECT_ALL_OPTION);
                    if (selectAllWasClicked) {
                        if (areAllFilteredSelected) {
                            // Deselect all filtered companies
                            const remainingCompanies = selectedCompanies.filter((company) => !filteredCompanies.includes(company));
                            onChange(remainingCompanies);
                        }
                        else {
                            // Select all filtered companies (add them to existing selection)
                            const newSelection = [
                                ...new Set([...selectedCompanies, ...filteredCompanies]),
                            ];
                            onChange(newSelection);
                        }
                    }
                    else {
                        // Normal selection change - remove SELECT_ALL_OPTION if it exists
                        const filteredValue = newValue.filter((val) => val !== SELECT_ALL_OPTION);
                        onChange(filteredValue);
                    }
                };
                return (React.createElement(Autocomplete, Object.assign({}, field, { multiple: true, options: options, value: selectedCompanies, onChange: handleChange, onInputChange: (_, newInputValue) => {
                        setSearchQuery(newInputValue);
                    }, filterOptions: (options) => options, disableCloseOnSelect: true, openOnFocus: true, getOptionLabel: (option) => {
                        if (option === SELECT_ALL_OPTION) {
                            return areAllFilteredSelected
                                ? "Deselecteer alles"
                                : "Selecteer alles";
                        }
                        return option;
                    }, renderOption: (props, option) => {
                        if (option === SELECT_ALL_OPTION) {
                            return (React.createElement(MenuItem, Object.assign({}, props, { key: SELECT_ALL_OPTION, sx: {
                                    fontWeight: 600,
                                    borderBottom: "1px solid",
                                    borderColor: "divider",
                                    backgroundColor: "action.hover",
                                } }),
                                React.createElement(Checkbox, { checked: areAllFilteredSelected }),
                                React.createElement(ListItemText, { primary: areAllFilteredSelected
                                        ? "Deselecteer alles"
                                        : "Selecteer alles" })));
                        }
                        const isSelected = selectedCompanies.includes(option);
                        return (React.createElement(MenuItem, Object.assign({}, props, { key: option }),
                            React.createElement(Checkbox, { checked: isSelected }),
                            React.createElement(ListItemText, { primary: option })));
                    }, renderInput: (params) => {
                        var _a;
                        return (React.createElement(TextField, Object.assign({}, params, { label: "Bedrijven", placeholder: "Zoek op naam...", error: !!errors.companies, helperText: (_a = errors.companies) === null || _a === void 0 ? void 0 : _a.message })));
                    }, renderTags: (value, getTagProps) => {
                        const filteredValue = value.filter((option) => option !== SELECT_ALL_OPTION);
                        // If all companies are selected, show a single chip with "Alle bedrijven geselecteerd"
                        if (isAllSelected &&
                            filteredValue.length === availableCompanies.length) {
                            return (React.createElement(Chip, { label: "Alle bedrijven geselecteerd", size: "small", onDelete: () => onChange([]), sx: { mr: 0.5 } }));
                        }
                        // Show max 3 chips, then show "+X meer"
                        const maxChipsToShow = 3;
                        const visibleChips = filteredValue.slice(0, maxChipsToShow);
                        const remainingCount = filteredValue.length - maxChipsToShow;
                        return (React.createElement(React.Fragment, null,
                            visibleChips.map((option, index) => (React.createElement(Chip, Object.assign({ label: option, size: "small" }, getTagProps({ index }), { sx: { mr: 0.5 } })))),
                            remainingCount > 0 && (React.createElement(Chip, { label: `+${remainingCount} meer`, size: "small", sx: { mr: 0.5 } }))));
                    } })));
            } })));
    const formActions = (React.createElement(Stack, { direction: "row", spacing: 2, width: "100%" },
        React.createElement(Button, { onClick: onClose, color: "inherit", variant: "outlined", fullWidth: true, size: "large" }, "Annuleren"),
        React.createElement(Button, { type: "submit", form: FORM_ID, variant: "contained", fullWidth: true, color: "primary", size: "large", disabled: !isValid }, editUser ? "Bijwerken" : "Opslaan")));
    if (isMobile) {
        return (React.createElement(FDrawer, { open: open, onClose: onClose, header: React.createElement(Typography, { variant: "h6", fontWeight: "bold" }, editUser ? "Gebruiker bewerken" : "Nieuwe gebruiker"), bottom: formActions },
            React.createElement("form", { id: FORM_ID, onSubmit: handleSubmit(onSubmit) }, formContent)));
    }
    return (React.createElement(Dialog, { open: open, onClose: onClose, maxWidth: "sm", fullWidth: true },
        React.createElement(DialogTitle, null, editUser ? "Gebruiker bewerken" : "Nieuwe gebruiker"),
        React.createElement(DialogContent, null,
            React.createElement("form", { id: FORM_ID, onSubmit: handleSubmit(onSubmit) }, formContent)),
        React.createElement(DialogActions, null, formActions)));
};
